import {breadcrumbsWithoutName, htmlEscape} from "@/shared/utils/string";
import {
	AddProjectTimeEvent,
	CombinedTimelineDayComponentConfig,
	DuplicateProjectTimeEvent,
	EditProjectTimeEvent,
	ProjectTimeItem,
	ProjectTimeTimelineDayContent,
	ProjectTimeTimelineItem,
	ProjectTimeTimelineItemType,
	QuickSwitchProjectTimeEvent,
	ShowProjectTimeEvent,
	ShowTaskDetailsEvent,
	StatusUpdatedEvent
} from "../timelineTypes";
import {TimelineContextMenu} from "../timelineContextMenu";
import {
	buildContextMenu,
	buildCustomFields,
	buildDescriptionElement,
	buildTimelineTimes,
	buildValidationsTable,
	deleteProjectTime,
	isProjectTimeDeletable,
	isProjectTimeDuplicateable,
	isProjectTimeEditable,
	isProjectTimeQuickSwitchable,
	isProjectTimeResumable,
	isProjectTimeStatusNumberEnabled,
	resumeProjectTime,
	setProjectTimeStatus,
	showProjectTimeQuickSwitchMessage
} from "../timelineHelper";
import {showProjectTimeBulkResult} from "@/shared/bulkOperationHelper";
import {
	EVENT_PROJECT_TIME_ADD,
	EVENT_PROJECT_TIME_DELETED,
	EVENT_PROJECT_TIME_DUPLICATE,
	EVENT_PROJECT_TIME_EDIT,
	EVENT_PROJECT_TIME_QUICK_SWITCH,
	EVENT_PROJECT_TIME_RESUMED,
	EVENT_PROJECT_TIME_SHOW,
	EVENT_PROJECT_TIME_SHOW_TASK_DETAILS,
	EVENT_PROJECT_TIME_STATUS_UPDATED
} from "../timelineConstants";
import {isProjectTimeDateTimeChangeAllowed, isValidationRelevant, shouldAddStatusNumber} from "@/shared/securityHelper";
import {minutesToMillis} from "@/shared/utils/misc";
import {formatDuration, formatRunningDurationSince, parseISOZonedDateTime, Temporal} from "@/shared/utils/datefunctions";
import {CombinedRecordingStatus, ProjectTime, ProjectTimeBulkOperationResult, RunningProjectTime} from "@/shared/timr";
import {DeletedStatus, RecordingProcedure, RunningTimeStatus, TimelineMode} from "@/shared/timrTypes";
import {toRecordedEntryStatus} from "@/shared/utils/recordMapper";

/**
 * Component to draw the project time items of a timeline day.
 * This component is used inside <code>ProjectTimeTimelineDay</code> to render the project time items for a specific day. If available this component is appended to the day container.
 * There are different item types:
 * - Running project time item: Represents the currently running project time entry.
 * - Project time item: Represents a completed project time entry.
 * - Gap item: Represents a gap in the timeline where no project time was recorded.
 * - Overlap item: Represents an overlap in the timeline.
 * In difference to <code>TimelineDayItems</code> a project time does not contain subitems.
 * For an overview of the timeline architecture, have a look into the UML diagram "timeline_web_components.png"
 */
export class ProjectTimeTimelineDayItems extends HTMLElement {
	private _timelineDay: ProjectTimeTimelineDayContent;
	private _config: CombinedTimelineDayComponentConfig;
	private _combinedRecordingStatus: CombinedRecordingStatus;

	set timelineDay(value: ProjectTimeTimelineDayContent) {
		this._timelineDay = value;
	}

	set config(value: CombinedTimelineDayComponentConfig) {
		this._config = value;
	}

	set combinedRecordingStatus(value: CombinedRecordingStatus) {
		this._combinedRecordingStatus = value;
	}

	/**
	 * Main entry point to render the project time timeline day items.
	 */
	public connectedCallback(): void {
		// the content of timelineDay and config are required must be set before rendering
		if (!this._timelineDay || !this._config) {
			return;
		}

		// sort items according to configuration (toggled via timeline control button and cached in session variable)
		const sortedItems = this._config.timelineSortDescending
			? this._timelineDay.items.slice().reverse()
			: this._timelineDay.items;

		const items = sortedItems
			.map(item => {
				switch (item.type) {
					case ProjectTimeTimelineItemType.RUNNING:
						return this.buildRunningItem(item);
					case ProjectTimeTimelineItemType.PROJECT_TIME:
						return this.buildProjectTimeItem(item);
					case ProjectTimeTimelineItemType.GAP:
						return this.buildGapItem(item);
					case ProjectTimeTimelineItemType.OVERLAP:
						return this.buildOverlapItem(item);
				}
			})
			.filter(Boolean);

		this.append(...items);
	}

	private buildRunningItem(item: ProjectTimeTimelineItem): HTMLElement {
		const {runningProjectTime} = item;
		if (!runningProjectTime) {
			return document.createElement("div");
		}

		const div = document.createElement("div");
		div.classList.add("item-project-time", "item-project-time-running", "item-running", runningProjectTime.billable ? "billable" : "not-billable");
		div.dataset.toggle = "context";

		const escapedTaskName = htmlEscape(runningProjectTime.task.name);
		const escapedBreadCrumbs = breadcrumbsWithoutName(runningProjectTime.taskBreadCrumbs || "", runningProjectTime.task.name);
		const escapedTaskBreadCrumbs = htmlEscape(runningProjectTime.taskBreadCrumbs || "");

		div.innerHTML =
			`
			${buildTimelineTimes(
				parseISOZonedDateTime(item.start),
				null,
				this._timelineDay.showTimeZone,
				this._config.timelineSortDescending
			)}
			<div class="panel panel-project-time">
				<div class="panel-heading">
							<div class="project-time-task" title="${escapedTaskBreadCrumbs}">
						<div>${escapedTaskName}</div>
						<div class="text-breadcrumbs text-nowrap" style="${!escapedBreadCrumbs ? "display:none" : ""}">${escapedBreadCrumbs}</div>
					</div>
					<div class="duration-pulse">
						${runningProjectTime.status === RunningTimeStatus.RUNNING ? `<span class="pulse-recording"></span>` : ""}
						${runningProjectTime.status === RunningTimeStatus.PAUSED ? `<i class="fa fa-pause"></i>` : ""}
					</div>
					<div class="duration-container">
						<span id="runningProjectTimeDuration">${formatRunningDurationSince(runningProjectTime)}</span>
						<div class="info-icons-container">
							${this._config.projectTimeAttachmentsEnabled && runningProjectTime.attachmentCount > 0 ? "<i class='far fa-image m-0'></i>" : ""}
						</div>
					</div>
					${buildContextMenu().outerHTML}
				</div>
				<div class="panel-body">
					${buildCustomFields(this._config.customFieldDefinitionsProjectTime, runningProjectTime).outerHTML}
				</div>
			</div>`;

		// add description if available (in weekly mode it is displayed in extra row since the space is limited, in daily in column via flexbox)
		const descriptionElement = buildDescriptionElement(runningProjectTime);
		if (descriptionElement) {
			if (this._config.mode === TimelineMode.Daily) {
				div.querySelector(".project-time-task").after(descriptionElement)
			} else {
				div.querySelector(".panel-body").prepend(descriptionElement);
			}
		}

		new TimelineContextMenu<RunningProjectTime>()
			.addMenuEntry({
				label: "report.showTaskDetails",
				action: () => this.onShowTaskDetailsClicked(runningProjectTime)
			})
			.bindToElement(div, runningProjectTime);

		return div;
	}

	private buildProjectTimeItem(item: ProjectTimeTimelineItem): HTMLElement {
		const projectTime = item.projectTime;
		if (!projectTime) {
			return document.createElement("div");
		}

		const div = document.createElement("div");
		div.classList.add("item-project-time", projectTime.billable ? "billable" : "not-billable");
		div.dataset.toggle = "context";

		const escapedTaskName = htmlEscape(projectTime.task.name);
		const escapedBreadCrumbs = breadcrumbsWithoutName(projectTime.taskBreadCrumbs || "", projectTime.task.name);
		const escapedTaskBreadCrumbs = htmlEscape(projectTime.taskBreadCrumbs || "");
		const positionAvailable =
			projectTime.startPosition?.id && projectTime.startPosition?.deletedStatus === DeletedStatus.NOT_DELETED ||
			projectTime.endPosition?.id && projectTime.endPosition?.deletedStatus === DeletedStatus.NOT_DELETED;

		div.innerHTML =
			`
			${buildTimelineTimes(
				parseISOZonedDateTime(item.start),
				parseISOZonedDateTime(item.end),
				this._timelineDay.showTimeZone,
				this._config.timelineSortDescending
			)}
			<div class="panel panel-project-time">
				<div class="panel-heading">
							<div class="project-time-task" title="${escapedTaskBreadCrumbs}">
						<div>${escapedTaskName}</div>
								<div class="text-breadcrumbs text-nowrap" style="${!escapedBreadCrumbs ? "display:none" : ""}">${escapedBreadCrumbs}</div>
					</div>
					<div class="breaktime-container" style="${projectTime.breakTime > 0 ? "" : "display:none"}">
						<i class="fal fa-coffee position-left"></i>${formatDuration(minutesToMillis(projectTime.breakTime))}
					</div>
					<div class="duration-container">
						<div class="display-flex">
							${projectTime.changed ? "<i class='fal fa-pen'></i>" : ""}
							${formatDuration(item.duration)}
						</div>
						<div class="info-icons-container">
							${this._config.projectTimeAttachmentsEnabled && projectTime.attachmentCount > 0 ? "<i class='far fa-image'></i>" : ""}
							${positionAvailable ? "<i class='far fa-map-marker-alt'></i>" : ""}
							<i class='far fa-status-${toRecordedEntryStatus(projectTime.status)}'></i>
						</div>
					</div>
					${buildContextMenu().outerHTML}
				</div>
				<div class="panel-body">
					${buildCustomFields(this._config.customFieldDefinitionsProjectTime, projectTime).outerHTML}
				</div>
				${buildValidationsTable(item.validationResults.filter(v => isValidationRelevant(v)), projectTime.id).outerHTML}
			</div>`;

		// add description if available (in weekly mode it is displayed in extra row since the space is limited, in daily in column via flexbox)
		const descriptionElement = buildDescriptionElement(projectTime);
		if (descriptionElement) {
			if (this._config.mode === TimelineMode.Daily) {
				div.querySelector(".project-time-task").after(descriptionElement)
			} else {
				div.querySelector(".panel-body").prepend(descriptionElement);
			}
		}

		const contextMenu = new TimelineContextMenu<ProjectTime>();
		if (this._config.recordingFunctionsActive) {
			contextMenu
				.addMenuEntry({
					label: messages.withArguments("timeline.resume", projectTime.task.name),
					show: projectTime => isProjectTimeResumable(this._combinedRecordingStatus, null, projectTime),
					action: projectTime => {
						resumeProjectTime(this._combinedRecordingStatus, null, projectTime)
							?.done(() => this.dispatchEvent(new CustomEvent(EVENT_PROJECT_TIME_RESUMED, {bubbles: true})));
					}
				})
				.addMenuEntry({
					label: messages.withArguments("timeline.quick.start", projectTime.task.name),
					show: projectTime => showProjectTimeQuickSwitchMessage("timeline.quick.start", this._combinedRecordingStatus, null, projectTime),
					active: projectTime => isProjectTimeQuickSwitchable(this._combinedRecordingStatus, null, projectTime),
					action: projectTime => this.onQuickSwitchProjectTimeClicked(projectTime)
				})
				.addMenuEntry({
					label: messages.withArguments("timeline.quick.switch", projectTime.task.name),
					show: projectTime => showProjectTimeQuickSwitchMessage("timeline.quick.switch", this._combinedRecordingStatus, null, projectTime),
					active: projectTime => isProjectTimeQuickSwitchable(this._combinedRecordingStatus, null, projectTime),
					action: projectTime => this.onQuickSwitchProjectTimeClicked(projectTime)
				})
				.addDivider();
		}
		contextMenu
			.addMenuEntry({
				label: "timeline.open",
				action: () => this.onShowProjectTimeClicked({projectTime: projectTime, validationResults: item.validationResults})
			})
			.addMenuEntry({
				label: "timeline.edit",
				active: () => isProjectTimeEditable(projectTime),
				action: () => this.onEditProjectTimeClicked({projectTime: projectTime, validationResults: item.validationResults})
			})
			.addMenuEntry({
				label: "report.duplicate",
				active: () => isProjectTimeDuplicateable(),
				action: () => this.onDuplicateProjectTimeClicked({
					projectTime: projectTime,
					validationResults: item.validationResults
				})
			})
			.addMenuEntry({
				label: "report.delete",
				active: () => isProjectTimeDeletable(projectTime),
				action: () => {
					deleteProjectTime(projectTime)
						.done(() => this.dispatchEvent(new CustomEvent(EVENT_PROJECT_TIME_DELETED, {bubbles: true})));
				}
			})
			.addDivider()
			.addMenuEntry({
				label: "report.showTaskDetails",
				show: () => !!projectTime.task?.id,
				action: () => this.onShowTaskDetailsClicked(projectTime)
			});

		const projectTimeStatusNumber = toRecordedEntryStatus(projectTime.status);

		// DEVNOTE: It is very likely that the use and the delivered value of the variable `isTaskLeader` is basically not implemented correctly and consistently; see documentation of method
		if (this._config.projectTimeStatusNumbers.some(statusNumber => shouldAddStatusNumber(statusNumber, projectTimeStatusNumber, false))) {
			contextMenu.addDivider();
		}

		for (const statusNumber of this._config.projectTimeStatusNumbers) {
			const statusName = messages["reports.filter.status." + statusNumber];
			const title = messages.withArguments("report.setStatus", statusName);

			contextMenu.addMenuEntry({
				label: title,
				// DEVNOTE: It is very likely that the use and the delivered value of the variable `isTaskLeader` is basically not implemented correctly and consistently; see documentation of method
				show: () => shouldAddStatusNumber(statusNumber, projectTimeStatusNumber, false),
				action: () => {
					setProjectTimeStatus(projectTime, statusNumber)
						.done((result: ProjectTimeBulkOperationResult) => {
							if (!result.allUpdated) {
								showProjectTimeBulkResult(result, "report.setStatus.");
							}
							if (result.updated > 0) {
								this.onStatusUpdated(statusName);
							}
						});
				},
				active: () => isProjectTimeStatusNumberEnabled(projectTime, statusNumber)
			});
		}

		contextMenu.bindToElement(div, projectTime);

		div.addEventListener("click", (e) => {
			if (e.target instanceof HTMLElement && !e.target.closest(".dropdown-menu")) {
				this.onShowProjectTimeClicked({projectTime: projectTime, validationResults: item.validationResults});
			}
		});

		return div;
	}

	private buildGapItem(item: ProjectTimeTimelineItem): HTMLElement {
		const div = document.createElement("div");

		if (this._timelineDay.showTimeZone) {
			div.classList.add("show-timezone");
		}

		div.classList.add("item-gap");
		div.innerHTML = `<div class="panel panel-gap pr-15">
								<div class="btn btn-link btn-link-primary btn-xs">
									<i class="fal fa-plus position-left" title="${messages["timeline.workingTime.addProjectTime"]}"></i> 
								</div>
								<div>${messages["timeline.projectTime.gap"]}</div>
								<div class="duration-container">${formatDuration(item.duration)}</div>
							</div>`;

		const addButton = div.querySelector(".btn");
		if (isProjectTimeDateTimeChangeAllowed(this._config.reportingOtherUsers)) {
			addButton.addEventListener("click", () => {
				if (isProjectTimeDateTimeChangeAllowed(this._config.reportingOtherUsers)) {
					this.onAddProjectTimeClicked(parseISOZonedDateTime(item.start), parseISOZonedDateTime(item.end));
				}
			});
		} else {
			div.classList.add("gap-disabled");
			addButton.classList.add("disabled");
		}

		return div;
	}

	private buildOverlapItem(item: ProjectTimeTimelineItem): HTMLElement {
		const div = document.createElement("div");

		if (this._timelineDay.showTimeZone) {
			div.classList.add("show-timezone");
		}

		div.classList.add("item-overlap");
		div.innerHTML = `
							<div class="panel panel-overlap justify-content-space-between pl-15 pr-15">
								<div><i class="fa fa-validation-severity-MINOR position-left" title="${messages["recordingValidation.severity.MINOR"]}"></i></div>
								<div>${messages["validationResult.entryOverlap.projectTime"]}</div>
								<div class="duration-container">${formatDuration(item.duration)}</div>
							</div>`;
		return div;
	}

	private onQuickSwitchProjectTimeClicked(projectTime: ProjectTime): void {
		const event = new CustomEvent<QuickSwitchProjectTimeEvent>(
			EVENT_PROJECT_TIME_QUICK_SWITCH, {
				detail: {
					quickSwitchProcedure: RecordingProcedure.ProjectTime,
					projectTime: projectTime
				},
				bubbles: true
			}
		);

		this.dispatchEvent(event);
	}

	private onShowProjectTimeClicked(projectTimeItem: ProjectTimeItem): void {
		const event = new CustomEvent<ShowProjectTimeEvent>(EVENT_PROJECT_TIME_SHOW, {
			detail: {
				projectTimeItem: projectTimeItem
			},
			bubbles: true
		});
		this.dispatchEvent(event);
	}

	private onEditProjectTimeClicked(projectTimeItem: ProjectTimeItem): void {
		const event = new CustomEvent<EditProjectTimeEvent>(EVENT_PROJECT_TIME_EDIT, {
			detail: {
				projectTimeItem: projectTimeItem
			},
			bubbles: true
		});
		this.dispatchEvent(event);
	}

	private onDuplicateProjectTimeClicked(projectTimeItem: ProjectTimeItem): void {
		const event = new CustomEvent<DuplicateProjectTimeEvent>(EVENT_PROJECT_TIME_DUPLICATE, {
			detail: {
				projectTimeItem: projectTimeItem
			},
			bubbles: true
		});
		this.dispatchEvent(event);
	}

	private onShowTaskDetailsClicked(projectTime: ProjectTime | RunningProjectTime): void {
		const event = new CustomEvent<ShowTaskDetailsEvent>(EVENT_PROJECT_TIME_SHOW_TASK_DETAILS, {
			detail: {
				projectTime: projectTime
			},
			bubbles: true
		});
		this.dispatchEvent(event);
	}

	private onStatusUpdated(statusName: string): void {
		const event = new CustomEvent<StatusUpdatedEvent>(EVENT_PROJECT_TIME_STATUS_UPDATED, {
			detail: {
				statusName: statusName
			},
			bubbles: true
		});
		this.dispatchEvent(event);
	}

	private onAddProjectTimeClicked(start: Temporal.ZonedDateTime | null, end: Temporal.ZonedDateTime | null): void {
		const event = new CustomEvent<AddProjectTimeEvent>(EVENT_PROJECT_TIME_ADD, {
			detail: {
				start: start,
				end: end
			},
			bubbles: true
		});
		this.dispatchEvent(event);
	}
}
